import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/87d05315-7468-4327-bf5b-c097536c3594/images/8028b5b15fd18c6edb266c30c277161f3272dd25bc5ce65e66e76261be9ff6b1.png";

const About = () => {
  const services = [
    "Logo Design",
    "Brochure Design",
    "Business Card Design",
    "Shirt Design",
    "Banner Design",
    "Custom Printing",
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="font-outfit text-4xl sm:text-5xl text-white mb-6">
                About <span className="gold-gradient">Velvet Press</span>
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Velvet Press specializes in producing high-quality, eye-catching graphic designs 
                and printing in The Bahamas and United States. Our experienced design and printing 
                team has years of industry experience and is dedicated to assisting our clients 
                in bringing their ideas to life.
              </p>
              <p className="text-gray-400 leading-relaxed">
                We offer a wide range of design services, including logo design, brochure design, 
                business card design, shirt design, and much more. Whether you need a custom design 
                for your business or a one-of-a-kind shirt design for a special event, we've got you covered.
              </p>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <img 
                src={ABOUT_IMG} 
                alt="Velvet Press Printing"
                className="w-full rounded-sm border border-white/10"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#0A0A0A] p-6 rounded-sm">
                <span className="font-outfit text-4xl font-bold">10+</span>
                <p className="text-sm font-medium">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-outfit text-3xl text-white mb-6">
                Our <span className="gold-gradient">Approach</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                We take pride in our ability to work closely with our clients to ensure that 
                their vision is fully realized in the final product. From concept to completion, 
                we are committed to providing exceptional customer service and top-quality designs 
                that are sure to impress.
              </p>
              <p className="text-gray-400 leading-relaxed mb-6">
                We understand that every project is unique, which is why we take a personalized 
                approach to every design. We use the latest software and printing technologies 
                to ensure that your design is vibrant, clear, and perfectly reproduced on the 
                chosen medium.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Lastly, we are committed to providing our clients with the highest level of 
                satisfaction. Contact us today to learn more about our services, and let us 
                help you create a design that will make a lasting impression!
              </p>
            </div>
            <div className="bg-[#141414] p-8 rounded-sm border border-white/10">
              <h3 className="font-outfit text-2xl text-white mb-6">Our Services</h3>
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-gray-300">{service}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/shop" 
                className="btn-gold inline-flex items-center mt-8"
                data-testid="about-shop-btn"
              >
                View All Products
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-outfit text-3xl text-white mb-12 text-center">
            Why Choose <span className="gold-gradient">Velvet Press</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Quality First",
                desc: "We use premium materials and the latest printing technology to ensure every product meets our high standards."
              },
              {
                title: "Customer Focused",
                desc: "Your satisfaction is our priority. We work closely with you to bring your vision to life."
              },
              {
                title: "Fast Turnaround",
                desc: "We understand time is valuable. Count on us for quick delivery without compromising quality."
              },
            ].map((value, index) => (
              <div 
                key={index} 
                className="p-8 bg-[#0A0A0A] border border-white/10 rounded-sm card-hover"
              >
                <h3 className="font-outfit text-xl text-[#D4AF37] mb-4">{value.title}</h3>
                <p className="text-gray-400">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-outfit text-3xl text-white mb-6">
            Ready to Start Your <span className="gold-gradient">Project?</span>
          </h2>
          <p className="text-gray-400 mb-8">
            Get in touch with us today and let's create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/request-quote" 
              className="btn-gold inline-flex items-center justify-center"
              data-testid="about-quote-btn"
            >
              Request a Quote
              <ArrowRight className="ml-2" size={18} />
            </Link>
            <Link 
              to="/contact" 
              className="btn-outline-gold inline-flex items-center justify-center"
              data-testid="about-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
